import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { getMatch } from '@/api/TournamentManagement/matchApi';
import { MatchStatus, MatchRole, AgeCategory, resolveEnum } from '@/models/tournament';
import type { MatchView, RefereeTeamMemberView } from '@/models/tournament';

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

interface TournamentMatchTableProps {
  matches: MatchView[];
  sortConfig: SortConfig;
  onSort: (key: string) => void;
  onViewDetails: (match: MatchView) => void;
  onEditMatch: (match: MatchView) => void;
  onDeleteMatch: (match: MatchView) => void;
  onCompleteMatch: (match: MatchView) => void;
  onReschedule: (match: MatchView) => void;
  onMarkAsClosed: (matchId: number) => void;
  onReturnForRevision: (matchId: number) => void;
  onCancelMatch: (matchId: number) => void;
  onChangeStatus?: (matchId: number, newStatus: MatchStatus) => void;
  /** Referees (non-HR) may only view and complete their own matches. Hides status change, edit, delete. */
  isReferee?: boolean;
  /** Current logged-in user id — referees only see their OWN work points on other-referee cards. */
  currentUserId?: string;
}

const TournamentMatchTable: React.FC<TournamentMatchTableProps> = ({
  matches,
  sortConfig,
  onSort,
  onViewDetails,
  onEditMatch,
  onDeleteMatch,
  onCompleteMatch,
  onReschedule,
  onMarkAsClosed,
  onReturnForRevision,
  onCancelMatch,
  onChangeStatus,
  isReferee,
  currentUserId,
}) => {
  const { t, i18n } = useTranslation('tournament');
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [expandedMatchData, setExpandedMatchData] = useState<Record<number, MatchView>>({});
  const [loadingExpanded, setLoadingExpanded] = useState<Record<number, boolean>>({});

  /** Normalise string or numeric MatchStatus coming from the API. */
  const rs = (status: MatchStatus | string): MatchStatus =>
    (resolveEnum(MatchStatus, status) as MatchStatus) ?? MatchStatus.Scheduled;

  const statusKeys: Record<MatchStatus, string> = {
    [MatchStatus.Scheduled]: 'Scheduled',
    [MatchStatus.InProgress]: 'In Progress',
    [MatchStatus.Completed]: 'Completed',
    [MatchStatus.Closed]: 'Closed',
    [MatchStatus.UnderRevision]: 'Under Revision',
    [MatchStatus.Cancelled]: 'Cancelled',
  };

  const roleKeys: Record<number, string> = {
    [MatchRole.HeadReferee]: 'Head Referee',
    [MatchRole.AssistantReferee]: 'Assistant Referee',
    [MatchRole.FourthOfficial]: 'Fourth Official',
    [MatchRole.Commissioner]: 'Commissioner',
  };

  const getStatusColor = (status: MatchStatus): string => {
    switch (status) {
      case MatchStatus.Scheduled:
        return 'bg-blue-100 text-blue-800';
      case MatchStatus.InProgress:
        return 'bg-yellow-100 text-yellow-800';
      case MatchStatus.Completed:
        return 'bg-green-100 text-green-800';
      case MatchStatus.Closed:
        return 'bg-gray-100 text-gray-800';
      case MatchStatus.UnderRevision:
        return 'bg-purple-100 text-purple-800';
      case MatchStatus.Cancelled:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSortIcon = (column: string) => {
    if (sortConfig.key !== column) {
      return <Icon name="ArrowUpDown" size={14} className="text-gray-400" />;
    }
    return sortConfig.direction === 'asc'
      ? <Icon name="ArrowUp" size={14} className="text-primary" />
      : <Icon name="ArrowDown" size={14} className="text-primary" />;
  };

  const formatDateTime = (dateTimeString: string): { date: string; time: string } => {
    const date = new Date(dateTimeString);
    return {
      date: date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }),
    };
  };

  const toggleRow = useCallback((matchId: number) => {
    setExpandedRows(prev => {
      if (prev.includes(matchId)) {
        return prev.filter(id => id !== matchId);
      }
      // Fetch full match details when expanding
      if (!expandedMatchData[matchId]) {
        setLoadingExpanded(l => ({ ...l, [matchId]: true }));
        getMatch(matchId)
          .then((result) => {
            if (result.errors.length === 0 && result.payload) {
              setExpandedMatchData(d => ({ ...d, [matchId]: result.payload }));
            }
          })
          .catch(() => {})
          .finally(() => setLoadingExpanded(l => ({ ...l, [matchId]: false })));
      }
      return [...prev, matchId];
    });
  }, [expandedMatchData]);

  if (matches.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center" data-testid="match-empty-state">
        <Icon name="Search" size={48} className="mx-auto text-muted-foreground/70 mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">{t('No matches found')}</h3>
        <p className="text-muted-foreground">{t('Try adjusting your filters or search terms')}</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm" data-testid="match-table">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th
                className="px-3 py-3 text-start text-xs font-medium text-foreground uppercase tracking-wider cursor-pointer hover:bg-muted"
                onClick={() => onSort('scheduledDateTime')}
              >
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span>{t('Date & Time')}</span>
                  {getSortIcon('scheduledDateTime')}
                </div>
              </th>
              <th
                className="px-3 py-3 text-start text-xs font-medium text-foreground uppercase tracking-wider cursor-pointer hover:bg-muted"
                onClick={() => onSort('location')}
              >
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span>{t('Location')}</span>
                  {getSortIcon('location')}
                </div>
              </th>
              <th className="px-3 py-3 text-start text-xs font-medium text-foreground uppercase tracking-wider">
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span>{t('Pitch')}</span>
                </div>
              </th>
              <th className="px-3 py-3 text-start text-xs font-medium text-foreground uppercase tracking-wider">
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span>{t('Age Group')}</span>
                </div>
              </th>
              <th className="px-3 py-3 text-start text-xs font-medium text-foreground uppercase tracking-wider">
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span>{t('Division')}</span>
                </div>
              </th>
              <th
                className="px-3 py-3 text-start text-xs font-medium text-foreground uppercase tracking-wider cursor-pointer hover:bg-muted"
                onClick={() => onSort('team1Name')}
              >
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span>{t('Opponents')}</span>
                  {getSortIcon('team1Name')}
                </div>
              </th>
              <th
                className="px-3 py-3 text-start text-xs font-medium text-foreground uppercase tracking-wider cursor-pointer hover:bg-muted"
                onClick={() => onSort('status')}
              >
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span>{t('Status')}</span>
                  {getSortIcon('status')}
                </div>
              </th>
              <th className="px-3 py-3 text-end text-xs font-medium text-foreground uppercase tracking-wider">
                {t('Actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {matches.map((match) => (
              <React.Fragment key={match.id}>
                {/* Main Row */}
                <tr
                  className="hover:bg-muted/50 transition-colors cursor-pointer"
                  data-testid={`match-row-${match.id}`}
                  onClick={() => toggleRow(match.id)}
                >
                  {/* Date & Time */}
                  <td className="px-3 py-3">
                    <div className="flex items-center">
                      <Icon
                        name={expandedRows.includes(match.id) ? 'ChevronDown' : 'ChevronRight'}
                        size={14}
                        className="text-muted-foreground me-2 flex-shrink-0 rtl:rotate-180"
                      />
                      <Icon name="Calendar" size={14} className="text-gray-400 me-2 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-foreground">{formatDateTime(match.scheduledDateTime).date}</p>
                        <p className="text-xs text-muted-foreground">
                          {match.scheduledTimeUnknown
                            ? <span className="italic">{t('Time TBD')}</span>
                            : formatDateTime(match.scheduledDateTime).time}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Location */}
                  <td className="px-3 py-3">
                    <div className="flex items-center">
                      <Icon name="MapPin" size={14} className="text-gray-400 me-2 flex-shrink-0" />
                      <span className="text-sm text-foreground">{match.location}</span>
                    </div>
                  </td>

                  {/* Pitch */}
                  <td className="px-3 py-3">
                    <span className="text-sm text-foreground">{match.pitch || '\u2014'}</span>
                  </td>

                  {/* Age Group */}
                  <td className="px-3 py-3">
                    <span className="text-sm text-foreground">
                      {match.ageCategory != null ? (AgeCategory[resolveEnum(AgeCategory, match.ageCategory) ?? -1] ?? '\u2014') : '\u2014'}
                    </span>
                  </td>

                  {/* Division */}
                  <td className="px-3 py-3">
                    <span className="text-sm text-foreground">{match.division || '\u2014'}</span>
                  </td>

                  {/* Opponents */}
                  <td className="px-3 py-3">
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <span className="text-sm font-medium text-foreground">{match.team1Name}</span>
                      <span className="text-xs text-muted-foreground">{t('vs')}</span>
                      <span className="text-sm font-medium text-foreground">{match.team2Name}</span>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(rs(match.status))}`}>
                      {t(statusKeys[rs(match.status)] ?? 'Unknown')}
                    </span>
                  </td>

                  {/* Actions — each button is status-aware */}
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    {(() => {
                      const status = rs(match.status);
                      // Terminal statuses: no in-place progression possible.
                      const isTerminal = status === MatchStatus.Closed || status === MatchStatus.Cancelled;
                      // "Has results entered" — Completed, Closed, or Under Revision.
                      const hasResults = status === MatchStatus.Completed || status === MatchStatus.Closed || status === MatchStatus.UnderRevision;
                      // Complete action only makes sense pre-results.
                      const canCompleteNow = status === MatchStatus.Scheduled || status === MatchStatus.InProgress || status === MatchStatus.UnderRevision;
                      // Update-results action replaces Complete once the match has results.
                      const canUpdateResults = hasResults && status !== MatchStatus.Closed && status !== MatchStatus.Cancelled;
                      // Edit/Delete/Reschedule — disabled for Closed and Cancelled (locked records).
                      const canEdit = !isTerminal;
                      const canDelete = !isTerminal;
                      const canReschedule = !isTerminal;
                      return (
                        <div className="flex items-center justify-end space-x-1 rtl:space-x-reverse">
                          <button
                            onClick={() => onViewDetails(match)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-blue-500/20"
                            title={t('View Details')}
                            data-testid={`match-view-btn-${match.id}`}
                          >
                            <Icon name="Eye" size={16} />
                          </button>
                          {/* Status change dropdown — HR/Admin only */}
                          {onChangeStatus && !isReferee && (
                            <select
                              value={String(status)}
                              onChange={(e) => {
                                const newStatus = parseInt(e.target.value) as MatchStatus;
                                if (newStatus !== status) {
                                  onChangeStatus(match.id, newStatus);
                                }
                              }}
                              className="text-xs border border-border rounded-md px-1.5 py-1 bg-background text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary cursor-pointer"
                              title={t('Change Status')}
                              data-testid={`match-status-select-${match.id}`}
                            >
                              <option value={String(MatchStatus.Scheduled)}>{t('Scheduled')}</option>
                              <option value={String(MatchStatus.InProgress)}>{t('In Progress')}</option>
                              <option value={String(MatchStatus.Completed)}>{t('Completed')}</option>
                              <option value={String(MatchStatus.Closed)}>{t('Closed')}</option>
                              <option value={String(MatchStatus.UnderRevision)}>{t('Under Revision')}</option>
                              <option value={String(MatchStatus.Cancelled)}>{t('Cancelled')}</option>
                            </select>
                          )}
                          {canUpdateResults ? (
                            <button
                              onClick={() => onCompleteMatch(match)}
                              className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-indigo-500/20"
                              title={t('Update Match Results')}
                              data-testid={`match-update-results-btn-${match.id}`}
                            >
                              <Icon name="RefreshCcw" size={16} />
                            </button>
                          ) : canCompleteNow ? (
                            <button
                              onClick={() => onCompleteMatch(match)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-green-500/20"
                              title={t('Complete Match')}
                              data-testid={`match-complete-btn-${match.id}`}
                            >
                              <Icon name="CheckCircle" size={16} />
                            </button>
                          ) : null}
                          {!isReferee && canReschedule && (
                            <button
                              onClick={() => onReschedule(match)}
                              className="p-1.5 text-violet-600 hover:bg-violet-50 rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-violet-500/20"
                              title={t('Reschedule Match')}
                              data-testid={`match-reschedule-btn-${match.id}`}
                            >
                              <Icon name="CalendarClock" size={16} />
                            </button>
                          )}
                          {!isReferee && canEdit && (
                            <button
                              onClick={() => onEditMatch(match)}
                              className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-amber-500/20"
                              title={t('Edit Match')}
                              data-testid={`match-edit-btn-${match.id}`}
                            >
                              <Icon name="Edit" size={16} />
                            </button>
                          )}
                          {!isReferee && canDelete && (
                            <button
                              onClick={() => onDeleteMatch(match)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-red-500/20"
                              title={t('Delete Match')}
                              data-testid={`match-delete-btn-${match.id}`}
                            >
                              <Icon name="Trash2" size={16} />
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                </tr>

                {/* Expanded Team Members Row */}
                {expandedRows.includes(match.id) && (() => {
                  const fullMatch = expandedMatchData[match.id];
                  const members = fullMatch?.refereeTeamMembers ?? match.refereeTeamMembers ?? [];
                  const isLoadingMembers = loadingExpanded[match.id];
                  return (
                  <tr className="bg-muted/50">
                    <td colSpan={8} className="px-3 py-3">
                      <div className="ms-8">
                        <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                          <Icon name="Users" size={16} className="me-2 text-primary" />
                          {t('Referee Team Members')} {isLoadingMembers ? '' : `(${members.length})`}
                          {isLoadingMembers && <Icon name="Loader2" size={14} className="ms-2 animate-spin text-primary" />}
                        </h4>
                        {members.length > 0 && !isLoadingMembers ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {members.map((member: RefereeTeamMemberView) => {
                              // Referees see work points for THEIR OWN row only; other referees' points are hidden.
                              const canSeeWorkPoints = !isReferee || member.userId === currentUserId;
                              return (
                              <div key={member.id} className="bg-card border border-border rounded-lg p-4 shadow-sm">
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex items-center">
                                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center me-3">
                                      <Icon name="User" size={18} className="text-primary" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-semibold text-foreground">{member.userName}</p>
                                      <p className="text-xs text-muted-foreground">{t(roleKeys[resolveEnum(MatchRole, member.role) ?? member.role] ?? 'Unknown')}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {canSeeWorkPoints && (
                                      <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                        {member.workPoints > 0 ? `${member.workPoints} ${t('pts')}` : t('—')}
                                      </div>
                                    )}
                                    {!isReferee && member.matchRating != null && member.matchRating > 0 && (
                                      <div className="flex items-center gap-0.5 ms-2">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <Icon
                                            key={star}
                                            name="Star"
                                            size={12}
                                            className={
                                              member.matchRating! >= star
                                                ? 'text-amber-400 fill-amber-400'
                                                : 'text-gray-300'
                                            }
                                          />
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {/* Override indicator before completion (no breakdown yet) — hidden when viewer can't see points */}
                                {canSeeWorkPoints && !member.workPointBreakdown && member.workPointOverride != null && (
                                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2 mb-3">
                                    <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                                      {t('Override')}: {member.workPointOverride} {t('pts')} — {t('Applied on completion')}
                                    </p>
                                  </div>
                                )}
                                {/* Work Point Breakdown */}
                                {canSeeWorkPoints && member.workPoints > 0 && member.workPointBreakdown && (() => {
                                  try {
                                    const bd = JSON.parse(member.workPointBreakdown);
                                    const isOverridden = bd.isOverridden || bd.IsOverridden;
                                    if (isOverridden) {
                                      return (
                                        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2 mb-3" data-testid={`breakdown-${member.id}`}>
                                          <p className="text-xs font-medium text-amber-800 dark:text-amber-300 mb-1">{t('Work Point Breakdown')}</p>
                                          <div className="flex flex-wrap gap-1.5 text-xs">
                                            <span className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 rounded font-semibold">{member.workPoints} {t('pts')}</span>
                                            <span className="text-gray-400">({t('calculated')}: {bd.calculatedPoints ?? bd.CalculatedPoints ?? 0})</span>
                                          </div>
                                        </div>
                                      );
                                    }
                                    return (
                                      <div className="bg-muted/50 rounded-lg p-2 mb-3" data-testid={`breakdown-${member.id}`}>
                                        <p className="text-xs font-medium text-foreground mb-1">{t('Work Point Breakdown')}</p>
                                        <div className="flex flex-wrap gap-1.5 text-xs">
                                          <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{t('Base')}: {bd.baseRolePoints ?? bd.BaseRolePoints ?? 0}</span>
                                          <span className="text-gray-400">+</span>
                                          <span className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">{t('Format')}: {bd.formatPoints ?? bd.FormatPoints ?? 0}</span>
                                          <span className="text-gray-400">+</span>
                                          <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">{t('Type')}: {bd.typeBonus ?? bd.TypeBonus ?? 0}</span>
                                          <span className="text-gray-400">+</span>
                                          <span className="bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">{t('Time')}: {bd.timeBonus ?? bd.TimeBonus ?? 0}</span>
                                          <span className="text-gray-400">+</span>
                                          <span className="bg-pink-50 text-pink-700 px-1.5 py-0.5 rounded">{t('Performance')}: {bd.performanceBonus ?? bd.PerformanceBonus ?? 0}</span>
                                          <span className="text-gray-400">=</span>
                                          <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded font-semibold">{t('Total')}: {member.workPoints}</span>
                                        </div>
                                      </div>
                                    );
                                  } catch {
                                    return null;
                                  }
                                })()}
                                <div className="space-y-2">
                                  {member.phone && (
                                    <div className="flex items-center text-xs text-muted-foreground">
                                      <Icon name="Phone" size={12} className="me-2 text-gray-400" />
                                      <span className="font-mono">{member.phone}</span>
                                    </div>
                                  )}
                                  {member.email && (
                                    <div className="flex items-center text-xs text-muted-foreground">
                                      <Icon name="Mail" size={12} className="me-2 text-gray-400" />
                                      <span className="truncate">{member.email}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        ) : !isLoadingMembers ? (
                          <p className="text-sm text-muted-foreground">{t('No referee team members assigned yet.')}</p>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                  );
                })()}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TournamentMatchTable;
